import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {Language} from 'auth-module/model/language';
import Color from 'color';
import {combineLatestWith, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {Link} from '../../../shared/model/link';
import {RenderedElement} from '../../../shared/model/rendered/rendered-element';
import {
  AppState, getCurrentTreeLanguages, getShowBackLinks, getTreeActiveLanguage
} from '../../../state-management/reducers';
import {getLinkColor} from '../shared/proman-link.service';

type LanguageMap = { [key: number]: Language };

@Component({
  selector: 'pm-link-overlay',
  templateUrl: './link-overlay.component.html',
  styleUrls: ['./link-overlay.component.css'],
  standalone: false
})
export class LinkOverlayComponent implements OnInit {
  @Input() node: RenderedElement;
  @Input() highlightInfo;

  // This will just be the first link if merged
  @Output('linkClicked') linkClicked = new EventEmitter<Link>();

  constructor(private store$: Store<AppState>, private translateService: TranslateService) {
  }

  protected showBackLinks$ = this.store$.select(getShowBackLinks);
  protected languages$ = this.store$.select(getCurrentTreeLanguages)
    .pipe(map(languages => languages.reduce((acc, lang) => ({
      ...acc,
      [lang.id]: lang
    }), {} as LanguageMap)));
  protected currentLanguage$ = this.store$.select(getTreeActiveLanguage);

  isDark(link: Link) {
    return Color(getLinkColor(link)).isDark();
  }

  get backLinks() {
    return this.node?.backLinks;
  }

  get links() {
    const node = this.node;
    let nodeLinks = node?.links;
    if (!!node?.info || !!node?.extendedInfo) {
      nodeLinks = [{
        id: '-1',
        label: (node.info || '') + (!!node.extendedInfo ? '\n...' : ''),
        linkType: 'info',
        action: '',
        color: node.infoColor
      }, ...nodeLinks];
    }

    return nodeLinks.filter(link => !link.adopted && !['watermark', 'logo'].includes(link.linkType));
  }

  ngOnInit() {
  }

  clicked($event, link: Link) {
    $event.preventDefault();
    if ($event.stopImmediatePropagation) {
      $event.stopImmediatePropagation();
    }
    this.linkClicked.emit(link);
  }

  // noinspection JSUnusedLocalSymbols
  getLinkId(index: number, link: Link): string {
    return link ? link.id : '';
  }

  getTooltip(link: Link): string {
    return !!link && link.label;
  }

  linkColor(link: Link): string {
    return getLinkColor(link).hex();
  }

  highlight(link: Link) {
    return this.highlightInfo && link.linkType === 'info';
  }

  prevent() {
    return false;
  }

  protected getBackLinkLabel(link: Link): Observable<string> {
    return this.languages$.pipe(combineLatestWith(this.currentLanguage$), switchMap(([languageMap, currentLanguage]) => {
      const linkLanguage = languageMap[link.languageID];
      if (currentLanguage.isDefault && !linkLanguage?.isDefault) {
        return this.translateService.get('link-overlay.backlinks.defaultLanguage', {
          ...link,
          languageName: linkLanguage.name
        });
      } else {
        return this.translateService.get('link-overlay.backlinks', link);
      }
    }));
  }


  protected readonly getTreeActiveLanguage = getTreeActiveLanguage;
}
